import UserInfo from './models/UserInfo'
import Response, { ResponseStatus } from '@api/Response'
import basicDataGateway, { UserBindedThirdAccountDTO, WXInfoQueryParam } from '@api/gateway/PlatformBasicData'
import defaultBasicDataGateway, { LoginType } from '@api/gateway/BasicData-default'
import { WaitToUpdateUserInfo } from './models/WaitToUpdateUserInfo'
import { WaitToRegisterAccount } from './models/WaitToRegisterAccount'
import { Action, getModule, Module, Mutation, VuexModule } from 'vuex-module-decorators'
import store from '@/store'
import { Role, RoleType, Secure } from '@api/Secure'
import WXInfo from './models/WXInfo'
import ConfigCenterModule from '@api/module/common/config-center/ConfigCenterModule'

const defaultDeadImg = ''

interface UserState {
  /**
   * 用户信息。存放当前登录帐号的信息
   */
  userInfo: UserInfo
  /**
   * 是否登陆
   */
  isLogin: boolean
  /**
   * 累计登录天数
   */
  userLoginDay: string
  /**
   * 用户第三方账号信息
   */
  thirdAccount: Array<UserBindedThirdAccountDTO>
  wxInfo: WXInfo
}

@Module({ namespaced: true, dynamic: true, name: 'UserModule', store })
class UserModule extends VuexModule implements UserState {
  userInfo = new UserInfo()
  isLogin = false
  userLoginDay = ''
  thirdAccount = new Array<UserBindedThirdAccountDTO>()
  wxInfo = new WXInfo('', '')

  /**
   * 请求加载用户信息
   */
  @Action
  @Role([RoleType.user])
  @Secure({
    PlatformBasicData: ['query.getCurrentUserInfo']
  })
  async queryUserInfo(): Promise<ResponseStatus> {
    if (this.userInfo.userId) {
      return new ResponseStatus(200)
    }
    const response = await basicDataGateway.getCurrentUserInfo()
    if (response.status.code === 401) {
      // 登录过期
      this.setUserInfo(new UserInfo())
      this.setIsLogin(false)
    }
    if (response.status.code === 200 && response.data) {
      // 请求成功
      const user = UserInfo.from(response.data)
      this.setUserInfo(user)
      if (user?.userId) {
        this.setIsLogin(true)
      } else {
        this.setIsLogin(false)
      }
    }
    return response.status
  }

  /**
   * 统计用户的登录天数
   * @param ctx
   */
  @Action
  @Role([RoleType.user])
  @Secure({
    PlatformBasicData: ['query.countUserLoginDay']
  })
  async queryCountUserLoginDay(): Promise<ResponseStatus> {
    const response = await basicDataGateway.countUserLoginDay()
    if (response.status.isSuccess()) {
      if (this.userInfo?.userId) {
        this.SET_USER_LOGIN_DAY({ userId: this.userInfo?.userId, day: response.data })
      }
    }
    return response.status
  }

  /**
   * 修改用户手机号
   */
  @Action
  @Role([RoleType.user])
  @Secure({
    'BasicData-default': ['mutation.updateStudent']
  })
  async doModifyUserInfoTel(phoneNumber: string): Promise<ResponseStatus> {
    const dto = this.userInfo.toStudentUpdateDTO()
    dto.phoneNumber = phoneNumber
    const response = await defaultBasicDataGateway.updateStudent(dto)
    if (response.status.isSuccess()) {
      this.setUserPhoneNumber(phoneNumber)
    }
    return response.status
  }

  /**
   * 修改用户头像
   */
  @Action
  @Role([RoleType.user])
  @Secure({
    'BasicData-default': ['mutations.updateStudent']
  })
  async modifyUserInfoImage(displayPhotoUrl: string): Promise<ResponseStatus> {
    const dto = this.userInfo.toStudentUpdateDTO()
    dto.displayPhotoUrl = displayPhotoUrl
    const response = await defaultBasicDataGateway.updateStudent(dto)
    if (response.status.isSuccess()) {
      this.SET_CURRENT_USER_PHOTO(displayPhotoUrl)
    }
    return response.status
  }

  /**
   * 请求获取微信信息
   * @param param
   */
  @Action
  @Role([RoleType.user])
  @Secure({
    PlatformBasicData: ['query.getWXUserInfo']
  })
  async getWXUserInfo(param: WXInfoQueryParam): Promise<ResponseStatus> {
    const response = await basicDataGateway.getWXUserInfo(param)
    if (response.status.isSuccess()) {
      // console.log(response)
      this.SET_WXINFO(new WXInfo(response.data.unionId, response.data.openId))
    }
    return response.status
  }

  @Action
  setWXInfo(param: WXInfo) {
    this.SET_WXINFO(param)
  }

  @Mutation
  private SET_WXINFO(param: WXInfo) {
    if (!param) {
      param = new WXInfo('', '')
    }
    this.wxInfo = param
  }

  @Mutation
  private setUserPhoneNumber(phoneNumber: string) {
    this.userInfo.phoneNumber = phoneNumber
  }

  @Mutation
  SET_CURRENT_USER_PHOTO(photo: string) {
    this.userInfo.displayPhotoUrl = photo
  }

  @Mutation
  SET_USER_LOGIN_DAY(params: { userId: string; day: number }) {
    this.userLoginDay = `${params.userId}#${params.day}`
  }

  /**
   * 修改用户信息
   */
  @Action
  @Role([RoleType.user])
  @Secure({
    'BasicData-default': ['mutation.updateStudent']
  })
  async doModifyUserInfo(update: WaitToUpdateUserInfo): Promise<ResponseStatus> {
    const dto = this.userInfo.toStudentUpdateDTO()
    update.mergeToStudentUpdateDTO(dto)
    const response = await defaultBasicDataGateway.updateStudent(dto)
    if (response.data) {
      this.setUserInfo(UserInfo.from(response.data))
    }
    return response.status
  }

  /**
   * 修改用户头像
   */
  @Action
  @Role([RoleType.user])
  @Secure({
    'BasicData-default': ['mutation.updateStudent']
  })
  async doModifyUserInfoImage(displayPhotoUrl: string): Promise<ResponseStatus> {
    const dto = this.userInfo.toStudentUpdateDTO()
    dto.displayPhotoUrl = displayPhotoUrl
    const response = await defaultBasicDataGateway.updateStudent(dto)
    if (response.status.isSuccess()) {
      this.setUserPhotoUrl(response.data.displayPhotoUrl)
    }
    return response.status
  }

  @Mutation
  private setUserPhotoUrl(displayPhotoUrl: string) {
    this.userInfo.displayPhotoUrl = displayPhotoUrl
  }

  /**
   * 注册用户，注册时具备带绑定第三方帐号功能。
   */
  @Action
  @Role([RoleType.user])
  @Secure({
    'BasicData-default': ['mutation.createStudentWithPhoneCaptchaToken']
  })
  async doRegisterUser(payload: { captchaToken: string; account: WaitToRegisterAccount }): Promise<ResponseStatus> {
    const dto = payload.account.toStudentCreateWithPhoneCaptchaDTO()
    const response = await defaultBasicDataGateway.createStudentWithPhoneCaptchaToken({
      captchaToken: payload.captchaToken,
      createWithPhoneCaptcha: dto
    })
    return response.status
  }

  /**
   * 注册用户同时绑定微信登录
   * @param ctx
   * @param payload
   */
  @Action
  @Role([RoleType.user])
  @Secure({
    'BasicData-default': ['mutation.createStudentAndBindWXWithPhoneCaptchaToken']
  })
  async doRegisterUserAndBindWXWithPhoneCaptchaToken(payload: {
    wxToken: string
    captchaToken: string
    account: WaitToRegisterAccount
  }): Promise<ResponseStatus> {
    const dto = payload.account.toStudentCreateWithPhoneCaptchaAndTokenRequest(payload.wxToken)
    const response = await defaultBasicDataGateway.createStudentAndBindWXWithPhoneCaptchaToken({
      captchaToken: payload.captchaToken,
      createWithPhoneCaptcha: dto
    })
    return response.status
  }

  /**
   * 使用微信token绑定当前账号
   * @param ctx
   * @param payload
   */
  @Action
  @Role([RoleType.user])
  @Secure({
    'BasicData-default': ['mutation.bindWXByTokenForOpenAPI'],
    PlatformBasicData: ['query.getCurrentUserBindedThirdAccounts']
  })
  async bindWXByTokenForOpenAPI(wxToken: string): Promise<ResponseStatus> {
    const response = await defaultBasicDataGateway.bindWXByTokenForOpenAPI(wxToken)
    if (response.status.isSuccess()) {
      await this.loadThirdAccount()
    }
    return response.status
  }

  /**
   * 使用登录账号和
   * 微信token绑定当前账号
   * @param ctx
   * @param payload
   */
  @Action
  @Role([RoleType.user])
  @Secure({
    'BasicData-default': ['mutation.bindWXByTokenAndLoginInputForOpenAPI'],
    PlatformBasicData: ['query.getCurrentUserBindedThirdAccounts']
  })
  async bindWXByTokenAndLoginInputForOpenAPI(params: {
    loginInput?: string
    token?: string
    dontLoad?: boolean
  }): Promise<ResponseStatus> {
    const response = await defaultBasicDataGateway.bindWXByTokenAndLoginInputForOpenAPI(params)
    if (response.status.isSuccess() && !params.dontLoad) {
      await this.loadThirdAccount()
    }
    return response.status
  }

  /**
   * 使用登录账号和
   * 微信openId和unionId绑定当前账号
   * @param ctx
   * @param payload
   */
  @Action
  @Role([RoleType.user])
  @Secure({
    'BasicData-default': ['mutation.bindWXOfficialAccountByOpenIdAndUnionIdLoginInputForOpenAPI'],
    PlatformBasicData: ['query.getCurrentUserBindedThirdAccounts']
  })
  async bindWXOfficialAccountByOpenIdAndUnionIdAndLoginInputForOpenAPI(params: {
    loginInput?: string
    openId?: string
    unionId?: string
    dontLoad?: boolean
    wxNickname?: string
  }): Promise<ResponseStatus> {
    const response = await defaultBasicDataGateway.bindWXOfficialAccountByOpenIdAndUnionIdLoginInputForOpenAPI(params)
    if (response.status.isSuccess() && !params.dontLoad) {
      await this.loadThirdAccount()
    }
    return response.status
  }

  /**
   * 使用登录账号和
   * 微信openId和unionId绑定当前账号
   * @param ctx
   * @param payload
   */
  @Action
  @Role([RoleType.user])
  @Secure({
    'BasicData-default': ['mutation.bindWXMiniProgramByOpenIdAndUnionIdLoginInputForOpenAPI'],
    PlatformBasicData: ['query.getCurrentUserBindedThirdAccounts']
  })
  async bindWXMiniProgramByOpenIdAndUnionIdAndLoginInputForOpenAPI(params: {
    loginInput?: string
    openId?: string
    unionId?: string
    dontLoad?: boolean
    wxNickname?: string
  }): Promise<ResponseStatus> {
    const response = await defaultBasicDataGateway.bindWXMiniProgramByOpenIdAndUnionIdLoginInputForOpenAPI(params)
    if (response.status.isSuccess() && !params.dontLoad && this.isLogin) {
      await this.loadThirdAccount()
    }
    return response.status
  }

  /**
   * 解绑微信
   * @param ctx
   */
  @Action
  @Role([RoleType.user])
  @Secure({
    'BasicData-default': ['mutation.unBindWX'],
    PlatformBasicData: ['query.getCurrentUserBindedThirdAccounts']
  })
  async unBindWX(): Promise<ResponseStatus> {
    const response = await defaultBasicDataGateway.unBindWX(this.userInfo.userId)
    if (response.status.isSuccess()) {
      await this.loadThirdAccount()
    }
    return response.status
  }

  /**
   * 验证帐号是否存在，验证手机号是否存在。
   */
  @Action
  @Role([RoleType.user])
  @Secure({
    'BasicData-default': ['query.loginInputExists']
  })
  async isPhoneNumberAccountExists(phoneNumber: string): Promise<Response<boolean>> {
    return defaultBasicDataGateway.loginInputExists({
      loginType: LoginType.PHONE,
      loginInput: phoneNumber
    })
  }

  /**
   * 验证是否绑定了指定类型的第三方账号
   * @param ctx
   * @param bindType；平台开放注册:11 ， QQ :21 ,微博 :31, 微信WEB:41，微信公众号:42，微信小程序:43，微信APP:44，其他:51
   */
  @Action
  @Role([RoleType.user])
  @Secure({
    PlatformBasicData: ['query.getCurrentUserBindedThirdAccounts']
  })
  async isBindThirdAccount(bindType: number): Promise<Response<boolean>> {
    const response = await basicDataGateway.getCurrentUserBindedThirdAccounts()
    const list: Array<UserBindedThirdAccountDTO> = response.data
    const result = new Response<boolean>()
    result.status = response.status
    if (response.status.isSuccess()) {
      result.data = !!list.find(dto => dto.connectType === bindType)
    }
    return result
  }

  /**
   * 加载当前用户的第三方账号
   */
  @Action
  @Role([RoleType.user])
  @Secure({
    PlatformBasicData: ['query.getCurrentUserBindedThirdAccounts']
  })
  async loadThirdAccount() {
    const response = await basicDataGateway.getCurrentUserBindedThirdAccounts()
    const list: Array<UserBindedThirdAccountDTO> = response.data
    if (response.status.isSuccess()) {
      this.SET_THIRD_ACCOUNT(list)
    }
    return response.status
  }

  @Mutation
  SET_THIRD_ACCOUNT(thirdAccount: Array<UserBindedThirdAccountDTO>) {
    this.thirdAccount = thirdAccount?.filter(p => p.available) || []
  }

  /**
   * 验证指定微信token是否绑定账号
   * @param ctx
   * @param bindType
   */
  @Action
  @Role([RoleType.user])
  @Secure({
    'BasicData-default': ['query.haveBindWXByTokenForOpenAPI']
  })
  async haveBindWXByTokenForOpenAPI(wxToken: string): Promise<Response<boolean>> {
    const response = await defaultBasicDataGateway.haveBindWXByTokenForOpenAPI(wxToken)
    return response
  }

  /**
   * 是否绑定微信第三方账号
   * @param openId
   * @param unionId
   * @param connectType 41：微信登录，42：微信公众号，43：微信小程序
   */
  @Action
  @Role([RoleType.user])
  @Secure({
    'BasicData-default': ['query.haveBindWXByOpenIdAndUnionIdForOpenAPI']
  })
  async haveBindWXByOpenIdAndUnionIdForOpenAPI(params: {
    openId?: string
    unionId?: string
    connectType: number
    wxNickname?: string
  }): Promise<Response<boolean>> {
    const response = await defaultBasicDataGateway.haveBindWXByOpenIdAndUnionIdForOpenAPI(params)
    return response
  }

  /**
   * 绑定第三方平台帐号。
   */
  @Action
  doBindThirdAccount() {
    // doSomething
    // return Promise
  }

  /**
   * 找回密码，传入验证码校验通过后获得的token和新密码
   */
  @Action
  @Role([RoleType.user])
  @Secure({
    'BasicData-default': ['mutation.resetPasswordWithPhoneCaptchaToken']
  })
  async doRetrievePassword(payload: { captchaToken: string; newPassword: string }): Promise<ResponseStatus> {
    const response = await defaultBasicDataGateway.resetPasswordWithPhoneCaptchaToken(payload)
    return response.status
  }

  /**
   * 修改密码
   */
  @Action
  @Role([RoleType.user])
  @Secure({
    'BasicData-default': ['mutation.changePassword']
  })
  async doModifyPassword(payload: { oldPassword: string; newPassword: string }): Promise<ResponseStatus> {
    const response = await defaultBasicDataGateway.changePassword(payload)
    return response.status
  }

  /**
   * 重置密码
   */
  @Action
  public async doResetPassword(newPassword: string): Promise<ResponseStatus> {
    const { status } = await defaultBasicDataGateway.resetPassword(newPassword)
    return Promise.resolve(status)
  }

  /**
   * 获取是否登录
   */
  @Action
  @Role([RoleType.user])
  @Secure({
    PlatformBasicData: ['query.isLogin']
  })
  async getIsLogin() {
    const response = await basicDataGateway.isLogin()
    const isLogin = response.data
    this.setIsLogin(isLogin)
  }

  /**
   * 退出登录
   */
  @Action
  doEXitLogin() {
    // 销毁token
    if (localStorage) {
      localStorage.removeItem('Access-Token')
      localStorage.removeItem('Refresh-Token')
    }
    this.setUserInfo(new UserInfo())
    this.setIsLogin(false)
  }

  /**
   * 登陆成功
   */
  @Action
  doLoginSuccess(userInfo: UserInfo) {
    this.setUserInfo(userInfo)
    this.setIsLogin(true)
  }

  @Mutation
  private setUserInfo(userInfo: UserInfo) {
    this.userInfo = userInfo
  }

  @Mutation
  private setIsLogin(isLogin: boolean) {
    this.isLogin = isLogin
  }

  get getUserPhotoUrl() {
    const prefix = ConfigCenterModule.getApplicationByName('application.resourcesPrefix')
    return this.userInfo.displayPhotoUrl ? `${prefix}${this.userInfo.displayPhotoUrl}` : defaultDeadImg
  }

  get getUserLoginDay() {
    const userId = this.userInfo?.userId
    const userLoginDay = this.userLoginDay
    if (userLoginDay) {
      const array = userLoginDay.split('#')
      if (array?.length > 0 && array[0] === userId) {
        return array[1]
      }
    }
    return 0
  }

  get isBind() {
    return (bindType: number) => {
      return !!this.thirdAccount?.find(dto => dto.connectType === bindType)
    }
  }

  /**
   * 根据绑定的第三方账号类别来获取该类别下的信息
   */
  get thirdAccountByBingType() {
    const find = this.thirdAccount.find(o => o.connectType === 41 || o.connectType === 42 || o.connectType === 43)
    if (find != undefined) {
      return find.nickname
    }
    return undefined
  }
}

export default getModule(UserModule)
